/**
 * usePermissionValidation Hook
 *
 * Custom hook for validating grant configurations
 */

import { useState, useCallback } from 'react';
import { grantService } from '../services/grantService';

export function usePermissionValidation() {
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [validationError, setValidationError] = useState(null);

  // Validate a single grant
  const validateGrant = useCallback(async (grant, mode = 'CREATE', clientId = null) => {
    try {
      setIsValidating(true);
      setValidationError(null);

      const result = await grantService.validateGrant(grant, mode, clientId);
      setValidationResult(result);

      return result;
    } catch (err) {
      console.error('Error validating grant:', err);
      setValidationError(err.message);

      const errorResult = {
        isValid: false,
        errors: [{
          code: 'VALIDATION_ERROR',
          message: err.message
        }],
        warnings: []
      };

      setValidationResult(errorResult);
      return errorResult;
    } finally {
      setIsValidating(false);
    }
  }, []);

  // Validate multiple grants
  const validateBatch = useCallback(async (grants, clientId = null) => {
    try {
      setIsValidating(true);
      setValidationError(null);

      const results = await Promise.all(
        grants.map(grant =>
          grantService.validateGrant(grant, 'CREATE', clientId).catch(err => ({
            isValid: false,
            errors: [{
              code: 'VALIDATION_ERROR',
              message: err.message
            }]
          }))
        )
      );

      return results;
    } catch (err) {
      console.error('Error validating batch:', err);
      setValidationError(err.message);
      return [];
    } finally {
      setIsValidating(false);
    }
  }, []);

  // Clear validation results
  const clearValidation = useCallback(() => {
    setValidationResult(null);
    setValidationError(null);
  }, []);

  // Validate grant rules
  const validateRules = useCallback((actionRules, resources) => {
    const errors = [];
    const warnings = [];

    // Check if action rules match resources
    const ruleResources = Object.keys(actionRules || {});
    const unmatchedRules = ruleResources.filter(r => !resources.includes(r));

    if (unmatchedRules.length > 0) {
      errors.push({
        code: 'UNMATCHED_RULES',
        message: `Action rules defined for non-existent resources: ${unmatchedRules.join(', ')}`
      });
    }

    // Check for resources without rules
    const resourcesWithoutRules = resources.filter(r => !ruleResources.includes(r));
    if (resourcesWithoutRules.length > 0) {
      warnings.push({
        code: 'NO_RULES',
        message: `Resources without action rules: ${resourcesWithoutRules.join(', ')}`
      });
    }

    // Check for DENY rules
    let hasDeny = false;
    Object.values(actionRules || {}).forEach(resourceRules => {
      Object.values(resourceRules).forEach(permission => {
        if (permission === 'DENY') {
          hasDeny = true;
        }
      });
    });

    if (hasDeny) {
      warnings.push({
        code: 'HAS_DENY',
        message: 'Grant contains DENY permissions which take precedence over ALLOW'
      });
    }

    return { errors, warnings };
  }, []);

  // Validate temporal constraints
  const validateTemporal = useCallback((effectiveFrom, effectiveTo) => {
    const errors = [];
    const warnings = [];
    const now = new Date();

    if (effectiveFrom && effectiveTo) {
      const from = new Date(effectiveFrom);
      const to = new Date(effectiveTo);

      if (from >= to) {
        errors.push({
          code: 'INVALID_TEMPORAL',
          message: 'Effective from date must be before effective to date'
        });
      }

      if (to < now) {
        warnings.push({
          code: 'EXPIRED',
          message: 'Grant has already expired'
        });
      }

      const daysUntilExpiry = Math.floor((to - now) / (1000 * 60 * 60 * 24));
      if (daysUntilExpiry > 0 && daysUntilExpiry <= 7) {
        warnings.push({
          code: 'EXPIRING_SOON',
          message: `Grant expires in ${daysUntilExpiry} days`
        });
      }
    }

    if (effectiveFrom && new Date(effectiveFrom) > now) {
      warnings.push({
        code: 'FUTURE_GRANT',
        message: 'Grant is not yet effective'
      });
    }

    return { errors, warnings };
  }, []);

  // Validate priority
  const validatePriority = useCallback((priority) => {
    const warnings = [];

    if (priority > 7) {
      warnings.push({
        code: 'HIGH_PRIORITY',
        message: `High priority (${priority}) may override other grants unexpectedly`
      });
    }

    if (priority === 10) {
      warnings.push({
        code: 'MAX_PRIORITY',
        message: 'Maximum priority - this grant will override all others'
      });
    }

    return warnings;
  }, []);

  // Validate target configuration
  const validateTarget = useCallback((grant) => {
    const errors = [];
    const warnings = [];

    if (grant.targetType === 'USER') {
      if (!grant.targetUserId) {
        errors.push({
          code: 'MISSING_USER_ID',
          message: 'User ID is required for USER type grants'
        });
      }

      if (grant.targetGroup || grant.targetGroupRole || grant.targetOrgLevel) {
        warnings.push({
          code: 'UNUSED_FIELDS',
          message: 'Group, role, and org level are ignored for USER type grants'
        });
      }
    } else if (grant.targetType === 'ROLE') {
      if (!grant.targetGroup) {
        errors.push({
          code: 'MISSING_GROUP',
          message: 'Group is required for ROLE type grants'
        });
      }

      if (grant.targetUserId) {
        warnings.push({
          code: 'UNUSED_USER_ID',
          message: 'User ID is ignored for ROLE type grants'
        });
      }

      // Check for overly broad permissions
      if (grant.targetGroup === 'ALL' &&
          grant.targetGroupRole === 'ALL' &&
          grant.targetOrgLevel === 'ALL') {
        warnings.push({
          code: 'BROAD_TARGET',
          message: 'Grant applies to all users - consider being more specific'
        });
      }
    }

    return { errors, warnings };
  }, []);

  // Comprehensive validation
  const validateComprehensive = useCallback((grant) => {
    const allErrors = [];
    const allWarnings = [];

    // Validate target
    const targetValidation = validateTarget(grant);
    allErrors.push(...targetValidation.errors);
    allWarnings.push(...targetValidation.warnings);

    // Validate rules
    if (grant.resources && grant.actionRules) {
      const rulesValidation = validateRules(grant.actionRules, grant.resources);
      allErrors.push(...rulesValidation.errors);
      allWarnings.push(...rulesValidation.warnings);
    }

    // Validate temporal
    if (grant.effectiveFrom || grant.effectiveTo) {
      const temporalValidation = validateTemporal(grant.effectiveFrom, grant.effectiveTo);
      allErrors.push(...temporalValidation.errors);
      allWarnings.push(...temporalValidation.warnings);
    }

    // Validate priority
    if (grant.priority) {
      const priorityWarnings = validatePriority(grant.priority);
      allWarnings.push(...priorityWarnings);
    }

    return {
      isValid: allErrors.length === 0,
      errors: allErrors,
      warnings: allWarnings
    };
  }, [validateTarget, validateRules, validateTemporal, validatePriority]);

  return {
    validateGrant,
    validateBatch,
    validateRules,
    validateTemporal,
    validatePriority,
    validateTarget,
    validateComprehensive,
    clearValidation,
    isValidating,
    validationResult,
    validationError
  };
}